import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api, formatDate, mediaUrl } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Btn, Loading, PageHero, SectionHead } from "@/components/public/Ui";
import NotFound from "@/pages/public/NotFound";

export function BlogIndex() {
  const [page, setPage] = useState(null);
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/public/pages/blog").then((r) => setPage(r.data)).catch(() => setPage(false));
    api.get("/public/posts", { params: { limit: 48 } }).then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(posts.map((p) => p.category).filter(Boolean)))],
    [posts]
  );
  const shown = filter === "All" ? posts : posts.filter((p) => p.category === filter);

  if (page === null) return <Loading />;
  const hero = page?.body_json?.hero || {};

  return (
    <>
      <Seo title={page?.seo_title} description={page?.seo_description} canonicalPath="/blog" />
      <PageHero
        overline={hero.overline}
        line1={hero.heading_line_1}
        line2={hero.heading_line_2}
        subheading={hero.subheading}
        image={mediaUrl(page?.hero_image_url)}
      />
      <div className="bka-container py-14 lg:py-20" data-testid="blog-index">
        <div className="mb-10 flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              data-testid={`blog-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setFilter(c)}
              className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                filter === c
                  ? "bg-bka-emerald text-white"
                  : "border border-slate-200 text-bka-navy hover:border-bka-emerald hover:text-bka-emerald"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {shown.length === 0 ? (
          <p data-testid="blog-empty" className="text-sm text-bka-ink/60">
            No posts published in this category yet.
          </p>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {shown.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="bka-card-lift group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={mediaUrl(p.image_url)}
                    alt={p.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <p className="bka-overline text-bka-emerald">{p.category}</p>
                  <h2 className="mt-3 font-display text-lg font-bold leading-snug text-bka-navy">{p.title}</h2>
                  <p className="mt-2.5 line-clamp-3 flex-1 text-sm leading-relaxed text-bka-ink/75">{p.excerpt}</p>
                  <p className="mt-5 text-xs font-semibold text-bka-ink/50">{formatDate(p.published_at)}</p>
                </div>
              </Link>
            ))}
          </div>
        )}

        {(page?.body_json?.news_links || []).length > 0 && (
          <section className="mt-16 rounded-[30px] border-t-[6px] border-bka-orange bg-bka-cream p-7 sm:p-10" data-testid="blog-news-links">
            <p className="bka-overline text-bka-orangeDark">From around the web</p>
            <h2 className="mt-2 font-display text-3xl font-black text-bka-navy sm:text-4xl">
              {page.body_json.news_heading || "Kidney News & Resources"}
            </h2>
            <div className="bka-accent-rule mt-5" />
            <p className="mt-5 max-w-2xl text-sm leading-[1.8] text-bka-ink/75">
              {page.body_json.news_intro ||
                "Trusted kidney health reporting and resources, selected by the Bahamas Kidney Association. Each link opens on the publisher's own website."}
            </p>
            <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {page.body_json.news_links.map((item, i) => (
                <a
                  key={item.url || i}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`blog-news-link-${i}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-bka-emerald/15 bg-white transition-colors duration-300 hover:border-bka-orange/50"
                >
                  {item.image_url && (
                    <div className="h-40 overflow-hidden">
                      <img
                        src={mediaUrl(item.image_url)}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-6">
                    <p className="text-xs font-extrabold uppercase tracking-[0.1em] text-bka-orangeDark">
                      {item.source}
                    </p>
                    <h3 className="mt-3 font-display text-lg font-bold leading-snug text-bka-navy">
                      {item.title}
                    </h3>
                    {item.summary && (
                      <p className="mt-2.5 flex-1 text-sm leading-relaxed text-bka-ink/75">{item.summary}</p>
                    )}
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-bka-emerald transition-colors group-hover:text-bka-orangeDark">
                      Read More
                      <ArrowUpRight className="h-4 w-4" />
                    </span>
                    {item.published_at && (
                      <span className="mt-2 text-xs font-semibold text-bka-ink/45">{item.published_at}</span>
                    )}
                  </div>
                </a>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/public/posts/${slug}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [slug]);

  if (data === null) return <Loading />;
  if (data === false) return <NotFound />;

  const { post, related } = data;

  return (
    <>
      <Seo
        title={post.seo_title || `${post.title} | Bahamas Kidney Association`}
        description={post.seo_description || post.excerpt}
        canonicalPath={`/blog/${slug}`}
      />
      <article data-testid={`blog-post-${slug}`}>
        <PageHero overline={post.category} line1={post.title} subheading={post.excerpt} image={mediaUrl(post.image_url)} />
        <div className="bka-container max-w-3xl py-14 lg:py-20">
          <div className="mb-10 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-slate-200 pb-6 text-sm text-bka-ink/60">
            <span className="font-bold text-bka-navy">{post.author || "Bahamas Kidney Association"}</span>
            <span aria-hidden="true">·</span>
            <time dateTime={post.published_at}>{formatDate(post.published_at)}</time>
          </div>
          <div className="bka-prose">
            {String(post.body || "")
              .split("\n\n")
              .map((p, i) => (
                <p
                  key={i}
                  dangerouslySetInnerHTML={{
                    __html: p
                      .replace(/&/g, "&amp;")
                      .replace(/</g, "&lt;")
                      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>"),
                  }}
                />
              ))}
          </div>
          <div className="mt-12 flex flex-wrap gap-3">
            <Btn to="/blog" variant="outline" testId="blog-post-back">
              All Posts
            </Btn>
            <Btn to="/donate" testId="blog-post-donate">
              Support Our Work
            </Btn>
          </div>
        </div>

        {related.length > 0 && (
          <div className="bg-bka-cream py-14 lg:py-20">
            <div className="bka-container">
              <SectionHead overline="Keep Reading" title="Related Posts" />
              <div className="grid gap-6 md:grid-cols-3">
                {related.map((p) => (
                  <Link
                    key={p.id}
                    to={`/blog/${p.slug}`}
                    data-testid={`related-post-${p.slug}`}
                    className="bka-card-lift rounded-2xl border border-slate-200 bg-white p-6"
                  >
                    <p className="bka-overline text-bka-emerald">{p.category}</p>
                    <h3 className="mt-3 font-display text-base font-bold leading-snug text-bka-navy">{p.title}</h3>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </article>
    </>
  );
}
